import { network } from '@utils'
import { handleReturn, MutationFunction, PaginatedResponse, QueryFunction } from './root'
import { VatRateLabel } from '@constants'

export interface IAutoCompleteAccount {
  id: number
  number: string
  name: string
}

interface AutoCompleteAccountProps {
  slug: string
}

export const fetchAutoCompleteAccounts: QueryFunction<
  AutoCompleteAccountProps,
  PaginatedResponse<IAutoCompleteAccount>
> = async ({ slug }, params = null) => {
  const url = `/v1/business/${slug}/account_auto_complete/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const fetchAccountSuggestions: QueryFunction<
  AutoCompleteAccountProps,
  IAutoCompleteAccount[]
> = async ({ slug }, params = null) => {
  const url = `/v1/business/${slug}/account_suggestions/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export interface ITranslatedName {
  key: string
  value: string
}

export interface IAccount {
  id: number
  name: string
  name_translations: ITranslatedName[]
  description: string
  number: number
  header_id?: number
  header_path: string[]
  has_dynamic_headers: boolean
  padded_number: number
  default_vat_code: number
  default_vat_rate: number
  default_vat_rate_label: VatRateLabel
  type: string
  opening_balance: number
  is_used: boolean
  is_shown: boolean
  balance: number
}

interface AccountListProps {
  slug: string
}

export const fetchAccounts: QueryFunction<AccountListProps, PaginatedResponse<IAccount>> = async (
  { slug },
  params = null
) => {
  const url = `/v1/business/${slug}/account/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

interface AccountInstanceProps {
  slug: string
  accountId: number
}

export const fetchAccount: QueryFunction<AccountInstanceProps, IAccount> = async (
  { slug, accountId },
  params = null
) => {
  const url = `/v1/business/${slug}/account/${accountId}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const createAccount: MutationFunction<AccountListProps, IAccount> = async (
  { slug },
  requestData
) => {
  const url = `/v1/business/${slug}/account/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const updateAccount: MutationFunction<AccountInstanceProps, IAccount> = async (
  { slug, accountId },
  requestData
) => {
  const url = `/v1/business/${slug}/account/${accountId}/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deleteAccount: MutationFunction<AccountInstanceProps, void> = async ({
  slug,
  accountId
}) => {
  const url = `/v1/business/${slug}/account/${accountId}/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'DELETE',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

interface ValidateAccountNumberProps {
  slug: string
  number: number
}

interface IAccountNumberValidation {
  exists: boolean
  data: IAccount
}

export const validateAccountNumber: QueryFunction<
  ValidateAccountNumberProps,
  IAccountNumberValidation
> = async ({ slug, number }) => {
  const url = `/v1/business/${slug}/account/validate_number/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params: { number }
  })
  return data
}

export const showAccount: MutationFunction<AccountInstanceProps, void> = async ({
  slug,
  accountId
}) => {
  const url = `/v1/business/${slug}/account/${accountId}/show/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const hideAccount: MutationFunction<AccountInstanceProps, void> = async ({
  slug,
  accountId
}) => {
  const url = `/v1/business/${slug}/account/${accountId}/hide/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export enum AccountBalanceSource {
  ENABLE_BANKING = 'ENABLE_BANKING',
  HOLVI = 'HOLVI'
}

interface IAccountBalanceResource {
  type: AccountBalanceSource
  id: number
}

export enum AccountBalanceType {
  CLAV = 'CLAV',
  CLBD = 'CLBD',
  FWAV = 'FWAV',
  INFO = 'INFO',
  ITAV = 'ITAV',
  ITBD = 'ITBD',
  OPAV = 'OPAV',
  OPBD = 'OPBD',
  OTHR = 'OTHR',
  PRCD = 'PRCD',
  VALU = 'VALU',
  XPCD = 'XPCD'
}

interface IBalance {
  account_id: number
  date: string
  fetched_at: string
  balance_type: AccountBalanceType
  amount: number
  currency: string
}
export interface IAccountBalance {
  name: string
  identifier: string
  identifier_short: string
  logo: string
  icon: string
  resource: IAccountBalanceResource
  balance: IBalance
}

interface AccountBalancesProps {
  slug: string
}

export const fetchAccountBalances: QueryFunction<AccountBalancesProps, IAccountBalance[]> = async ({
  slug
}) => {
  const url = `/v1/business/${slug}/account/balances/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'GET',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export interface IBalanceForecastDay {
  id: string
  date: string
  /**
   * Euros, the same unit `fetchAccountBalances` answers in. The endpoint
   * builds this with `Money.from_subunits(...).amount`, which has already
   * divided by the currency subunit ratio.
   */
  balance: number
  current: boolean
}

export interface IBalanceForecast {
  id: string
  account_id: number | null
  days: IBalanceForecastDay[]
}

interface BalanceForecastProps {
  slug: string
  accountId?: number | null
}

export const fetchBalanceForecast: QueryFunction<BalanceForecastProps, IBalanceForecast> = async ({
  slug,
  accountId
}) => {
  const url = `/v1/business/${slug}/account/balance_forecast/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'GET',
    params: accountId ? { account_id: accountId } : {},
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}
